import { useState } from 'react';
import {
  Table, Select, Space, Tag, Input, DatePicker, Card, Row, Col,
  Statistic, Button, Modal, Typography, message, Popconfirm
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, DeleteOutlined,
  WarningOutlined, InfoCircleOutlined, BugOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { agentLogsApi, type AgentLogDto } from '../../api/agentLogs';
import { agentsApi } from '../../api/agents';
import dayjs from 'dayjs';
import { FilterPanel } from '../FilterPanel';
import { JsonViewer } from '../JsonViewer';
import { ExportButton } from '../ExportButton';
import { formatDate } from '../../utils/export';

const { RangePicker } = DatePicker;
const { Text } = Typography;

export function FirmLogsPanel({ firmId }: { firmId: number }) {
  const [selectedAgentId, setSelectedAgentId] = useState<number | undefined>(undefined);
  const [selectedLevel, setSelectedLevel] = useState<string | undefined>(undefined);
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [selectedLog, setSelectedLog] = useState<AgentLogDto | null>(null);
  const queryClient = useQueryClient();

  const { data: agents = [] } = useQuery({
    queryKey: ['agents', firmId],
    queryFn: () => agentsApi.getByFirm(firmId),
    enabled: !!firmId,
  });

  const { data: logsResponse, isLoading } = useQuery({
    queryKey: ['agent-logs', firmId, selectedAgentId, selectedLevel, searchText, dateRange, page, pageSize],
    queryFn: () => agentLogsApi.getLogs(firmId, {
      agentId: selectedAgentId,
      level: selectedLevel,
      search: searchText,
      from: dateRange?.[0]?.toISOString(),
      to: dateRange?.[1]?.toISOString(),
      page,
      pageSize,
    }),
    enabled: !!firmId,
  });

  const { data: stats } = useQuery({
    queryKey: ['agent-logs-stats', firmId],
    queryFn: () => agentLogsApi.getStats(firmId),
    enabled: !!firmId,
  });

  const clearLogsMutation = useMutation({
    mutationFn: (days: number) => agentLogsApi.clearLogs(firmId, days),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['agent-logs'] });
      message.success(`${result.deleted} log silindi`);
    },
    onError: () => message.error('Loglar silinemedi'),
  });

  const getLevelIcon = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error':
      case 'critical':
        return <WarningOutlined style={{ color: '#ff4d4f' }} />;
      case 'warning':
        return <WarningOutlined style={{ color: '#faad14' }} />;
      case 'debug':
        return <BugOutlined style={{ color: '#8c8c8c' }} />;
      default:
        return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
    }
  };

  const getLevelColor = (level: string): string => {
    switch (level.toLowerCase()) {
      case 'error':
        return 'red';
      case 'critical':
        return 'magenta';
      case 'warning':
        return 'orange';
      case 'debug':
        return 'default';
      default:
        return 'blue';
    }
  };

  const columns = [
    {
      title: 'Zaman',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString('tr-TR'),
    },
    {
      title: 'Seviye',
      dataIndex: 'level',
      key: 'level',
      width: 100,
      render: (level: string) => (
        <Tag color={getLevelColor(level)} icon={getLevelIcon(level)}>
          {level}
        </Tag>
      ),
    },
    {
      title: 'Agent',
      dataIndex: 'agentName',
      key: 'agentName',
      width: 150,
    },
    {
      title: 'Kaynak',
      dataIndex: 'source',
      key: 'source',
      width: 120,
      render: (source: string | null) => source || '-',
    },
    {
      title: 'Mesaj',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      render: (message: string, record: AgentLogDto) => (
        <a onClick={() => setSelectedLog(record)} style={{ color: 'inherit' }}>
          {message}
        </a>
      ),
    },
    {
      title: 'Detay',
      key: 'details',
      width: 80,
      render: (_: unknown, record: AgentLogDto) => (
        record.details ? (
          <Button type="link" size="small" onClick={() => setSelectedLog(record)}>
            Göster
          </Button>
        ) : '-'
      ),
    },
  ];

  const levelStats = stats?.byLevel || [];
  const errorCount = levelStats.find(s => s.level === 'Error')?.count || 0;
  const warningCount = levelStats.find(s => s.level === 'Warning')?.count || 0;
  const infoCount = levelStats.find(s => s.level === 'Info')?.count || 0;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => queryClient.invalidateQueries({ queryKey: ['agent-logs'] })}
          >
            Yenile
          </Button>
          <Popconfirm
            title="Eski logları sil"
            description="30 günden eski loglar silinecek"
            onConfirm={() => clearLogsMutation.mutate(30)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button danger icon={<DeleteOutlined />}>
              Temizle
            </Button>
          </Popconfirm>
        </Space>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Hata (24 Saat)"
              value={errorCount}
              valueStyle={{ color: errorCount > 0 ? '#ff4d4f' : '#52c41a' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Uyarı (24 Saat)"
              value={warningCount}
              valueStyle={{ color: warningCount > 0 ? '#faad14' : '#52c41a' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Bilgi (24 Saat)"
              value={infoCount}
              prefix={<InfoCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <FilterPanel
        activeFilterCount={
          [selectedAgentId, selectedLevel, dateRange, searchText].filter(Boolean).length
        }
        onClearFilters={() => {
          setSelectedAgentId(undefined);
          setSelectedLevel(undefined);
          setDateRange(null);
          setSearchText(undefined);
          setPage(1);
        }}
      >
        <Select
          style={{ width: 150 }}
          placeholder="Agent"
          value={selectedAgentId}
          onChange={(value) => { setSelectedAgentId(value); setPage(1); }}
          options={agents.map(a => ({ value: a.id, label: a.name }))}
          allowClear
        />
        <Select
          style={{ width: 120 }}
          placeholder="Seviye"
          value={selectedLevel}
          onChange={(value) => { setSelectedLevel(value); setPage(1); }}
          options={[
            { value: 'Debug', label: 'Debug' },
            { value: 'Info', label: 'Info' },
            { value: 'Warning', label: 'Warning' },
            { value: 'Error', label: 'Error' },
            { value: 'Critical', label: 'Critical' },
          ]}
          allowClear
        />
        <RangePicker
          showTime
          format="DD.MM.YYYY HH:mm"
          value={dateRange}
          onChange={(dates) => {
            setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null);
            setPage(1);
          }}
        />
        <Input
          style={{ width: 200 }}
          placeholder="Mesajda ara..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value || undefined)}
          onPressEnter={() => setPage(1)}
          allowClear
        />
        <ExportButton
          data={logsResponse?.data || []}
          filename="agent-logs"
          columns={[
            { key: 'id', title: 'ID' },
            { key: 'createdAt', title: 'Zaman', render: formatDate },
            { key: 'level', title: 'Seviye' },
            { key: 'agentName', title: 'Agent' },
            { key: 'source', title: 'Kaynak' },
            { key: 'message', title: 'Mesaj' },
            { key: 'details', title: 'Detaylar' },
          ]}
        />
      </FilterPanel>

      {/* Logs Table */}
      <Table
        columns={columns}
        dataSource={logsResponse?.data || []}
        rowKey="id"
        loading={isLoading}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: logsResponse?.pagination.totalCount || 0,
          showSizeChanger: true,
          showTotal: (total) => `Toplam ${total} log`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        size="small"
        locale={{ emptyText: 'Log bulunamadı' }}
        rowClassName={(record) =>
          record.level === 'Error' || record.level === 'Critical'
            ? 'ant-table-row-error'
            : ''
        }
      />

      {/* Log Detail Modal */}
      <Modal
        title="Log Detayı"
        open={!!selectedLog}
        onCancel={() => setSelectedLog(null)}
        footer={[
          <Button key="close" onClick={() => setSelectedLog(null)}>
            Kapat
          </Button>
        ]}
        width={700}
      >
        {selectedLog && (
          <div>
            <Row gutter={[16, 8]}>
              <Col span={8}>
                <Text type="secondary">Zaman:</Text>
              </Col>
              <Col span={16}>
                <Text>{new Date(selectedLog.createdAt).toLocaleString('tr-TR')}</Text>
              </Col>

              <Col span={8}>
                <Text type="secondary">Agent:</Text>
              </Col>
              <Col span={16}>
                <Text>{selectedLog.agentName}</Text>
              </Col>

              <Col span={8}>
                <Text type="secondary">Seviye:</Text>
              </Col>
              <Col span={16}>
                <Tag color={getLevelColor(selectedLog.level)}>{selectedLog.level}</Tag>
              </Col>

              <Col span={8}>
                <Text type="secondary">Kaynak:</Text>
              </Col>
              <Col span={16}>
                <Text>{selectedLog.source || '-'}</Text>
              </Col>
            </Row>

            <div style={{ marginTop: 16 }}>
              <Text type="secondary">Mesaj:</Text>
              <div style={{ marginTop: 8, background: '#f5f5f5', padding: 12, borderRadius: 4 }}>
                {selectedLog.message}
              </div>
            </div>

            {selectedLog.details && (
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">Detaylar:</Text>
                <div style={{ marginTop: 8 }}>
                  <JsonViewer data={selectedLog.details} defaultExpanded />
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <style>{`
        .ant-table-row-error {
          background-color: #fff1f0 !important;
        }
        .ant-table-row-error:hover > td {
          background-color: #ffccc7 !important;
        }
      `}</style>
    </div>
  );
}
